import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { SearchComponent } from '@connect-services/client/components/search/search.component';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { CapitalizePipe } from '@connect-services/client/pipes/capitalize.pipe';
import { ConnieUiService } from '@connect-services/client/services/connie-ui.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { ButtonComponent } from '@connect-services/src/lib/components/button/button.component';
import { ConnieService, WidgetConfig } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

declare global {
    interface Window {
        __chainlitBundleLoaded: boolean;
        __connieMounted: boolean;
        __cq_authFetchInstalled: boolean;
        __cq_chainlit_cfg: WidgetConfig | null;
        __cq_resumeThreadInCopilot: (threadId: string) => Promise<void>;
        clearChainlitCopilotThreadId: (currentThreadId?: string) => void;
        currentProjectId: string;
        currentSessionId: string;
        currentUserId: string;
        currentUserName: string;
        getChainlitCopilotThreadId?: () => string;
        mountChainlitWidget: (params: object) => void;
        toggleChainlitCopilot: () => void;
        unmountChainlitWidget?: () => void;
    }

    interface ShadowRoot {
        __copilotObserver: MutationObserver | null;
        __copilotObserverPaused: MutationObserver | null;
        __cqResizeObs: ResizeObserver | null;
        __cqResizeObsPaused: ResizeObserver | null;
        __graphHighlightAttached?: boolean;
        __hlKeydown?: boolean;
        __hlObserver?: MutationObserver;
    }

    interface HTMLElement {
        __hlAttached?: boolean;
    }
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CapitalizePipe,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        SearchComponent,
    ],
    selector: 'app-connie',
    styleUrl: './connie.component.scss',
    templateUrl: './connie.component.html',
})
export class ConnieComponent {
    private static readonly THREAD_ID_LOCAL_STORAGE_KEY = 'chainlit-copilot-thread-id';

    protected readonly ActionsEnum = ActionsEnum;
    protected readonly connieUiService = inject(ConnieUiService);
    protected readonly copilotRef = signal<HTMLElement | null>(null);
    protected readonly currentThreadId = signal<string | null>(null);
    protected readonly hasConnieDataInformation = signal<boolean>(false);
    protected readonly isLoading = signal<boolean>(false);
    protected readonly translateService = inject(TranslateService);

    private readonly connieService = inject(ConnieService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly panelRef = viewChild.required<ElementRef>('copilotPanel');
    private readonly projectBloc = inject(ProjectBloc);
    private readonly securityService = inject(SecurityService);

    public constructor() {
        window.__chainlitBundleLoaded = false;
        const escapeHandler = this.getEscapeHandler();
        window.addEventListener('keydown', escapeHandler, { capture: true });

        this.initResumeThreadMethod();

        this.destroyRef.onDestroy(() => {
            window.removeEventListener('keydown', escapeHandler, { capture: true });
            this.connieUiService.clearChainlitContext();
            this.connieUiService.clearSignals();
        });

        effect(() => {
            if (!this.securityService.meSignal() || !this.projectBloc.value()) {
                return;
            }

            window.currentUserId = this.securityService.meSignal()!.email;
            window.currentUserName = this.securityService.meSignal()!.fullName;
            window.currentProjectId = this.projectBloc.value()!.id;
            window.currentSessionId = sessionStorage.getItem('session')!;
        });

        effect(async () => {
            if (!this.securityService.meSignal()?.user) {
                return;
            }

            const connieDataInformation = await firstValueFrom(
                this.connieService.connieControllerGetDataInformationForConnie({
                    project: this.projectBloc.value()?.id,
                    user: this.securityService.meSignal()!.user!,
                }),
            );

            this.hasConnieDataInformation.set(!!Object.keys(connieDataInformation.depositFoldersInformation).length);

            this.resetChainlitData();
            await this.loadAndDisplayChainlit();
        });
    }

    public closeHistoryPanel(): void {
        this.connieUiService.isHistoryOpen.set(false);
        const shadowRoot = this.connieUiService.getShadowRoot();

        this.connieUiService.state.set({ ...this.connieUiService.state(), cursor: null });
        this.connieUiService.threads.set([]);

        if (!shadowRoot) {
            return;
        }

        this.connieUiService.enhanceChatUI(shadowRoot);
    }

    public async openThread(threadId: string): Promise<void> {
        await window.__cq_resumeThreadInCopilot?.(threadId);
    }

    public async searchValueChange(search: string): Promise<void> {
        this.connieUiService.state.set({ ...this.connieUiService.state(), search });
        await this.connieUiService.setThreadHistory();
    }

    private clearStoredThreadId(): void {
        // Prefer official helper
        try {
            if (typeof window.clearChainlitCopilotThreadId === 'function') {
                window.clearChainlitCopilotThreadId();
            }
        } catch {
            // empty : do nothing to continue cleaning
        }

        // also clear the actual storage key
        try {
            localStorage.removeItem(ConnieComponent.THREAD_ID_LOCAL_STORAGE_KEY);
        } catch {
            // empty : do nothing to avoid breaking connie initialization
        }
    }

    private delay(ms: number): Promise<unknown> {
        return new Promise((r) => setTimeout(r, ms));
    }

    private async getChainlitConfig(): Promise<WidgetConfig | undefined> {
        this.isLoading.set(true);

        const embedTokenResult: { embedToken: string } = await this.getEmbedToken();

        if (!embedTokenResult) {
            return;
        }

        return this.getWidgetConfig(embedTokenResult.embedToken);
    }

    private getEmbedToken(): Promise<{ embedToken: string }> {
        return firstValueFrom(
            this.connieService.connieControllerPostEmbedToken({
                embedTokenDto: {
                    name: this.securityService.meSignal()!.fullName,
                    projectId: this.projectBloc.value()!.id,
                    role: 'user',
                    userEmail: this.securityService.meSignal()!.email,
                },
            }),
        );
    }

    private getEscapeHandler(): (event: KeyboardEvent) => void {
        return (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') {
                return;
            }

            const root = this.connieUiService.getShadowRoot();
            if (!root) {
                return;
            }

            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();

                return;
            }
            event.stopPropagation();
        };
    }

    private getWidgetConfig(embedToken: string): Promise<WidgetConfig> {
        return firstValueFrom(
            this.connieService.connieControllerGetWidgetConfig({
                sessionId: sessionStorage.getItem('session')!,
                theme: 'light',
                xEmbedToken: embedToken,
            }),
        );
    }

    /**
     * Public helper: resume a given thread id WITHOUT remounting the widget.
     * Usage: window.__cq_resumeThreadInCopilot('<thread-id>')
     */
    private initResumeThreadMethod(): void {
        window.__cq_resumeThreadInCopilot = (threadId: string): Promise<void> => {
            let __cq_switchLock = Promise.resolve();

            // Serialize concurrent calls to avoid race conditions
            __cq_switchLock = __cq_switchLock.then(async () => {
                // 1) Ensure the Copilot dialog is open
                if (!this.isCopilotOpen()) {
                    window.toggleChainlitCopilot?.();
                    await this.waitForCopilotShadowAsync({ delay: 100, maxAttempts: 120 });
                }

                // 2) Wait until Chainlit exposes the switch helper & the tree is stable
                await this.waitUntil(() => typeof window.clearChainlitCopilotThreadId === 'function');
                await this.waitForStableWidget({ frames: 2, idleMs: 60 });

                const shadow = this.connieUiService.getShadowRoot();

                if (shadow) {
                    this.suspendEnhancers(shadow);
                }

                try {
                    // 3) Switch thread on the next frame (avoid mid-render updates)
                    await this.requestAnimationFrameAsync();
                    if (threadId !== this.currentThreadId()) {
                        window.clearChainlitCopilotThreadId(threadId);
                    }
                } catch {
                    localStorage.setItem('chainlit:threadId', threadId);
                } finally {
                    // Give React time to commit the new tree, then resume our observers
                    await this.requestAnimationFrameAsync(); // commit frame
                    await this.waitForStableWidget(); // settle streaming/layout a bit
                    if (shadow) {
                        this.resumeEnhancers(shadow);
                    }
                }

                // 4) Make sure the dialog didn't close due to anything external
                if (!this.isCopilotOpen()) {
                    window.toggleChainlitCopilot?.();
                    await this.waitForCopilotShadowAsync({ delay: 100, maxAttempts: 120 });
                }
                this.currentThreadId.set(threadId);
            });

            // Wait until this call's turn is complete
            return __cq_switchLock;
        };
    }

    private injectStylesIntoChainlitShadow(): void {
        const host = document.getElementById('chainlit-copilot');
        if (!host) {
            return;
        }

        const shadowRoot: ShadowRoot | null = host.shadowRoot;
        if (!shadowRoot) {
            return;
        }

        this.connieUiService.injectHeaderHiderCSS(shadowRoot);
        this.connieUiService.enhanceChatUI(shadowRoot);
        this.connieUiService.observeShadow(shadowRoot);
    }

    private installAuthFetch(chainlitServer: string, accessToken: string): Promise<Response> | void {
        if (window.__cq_authFetchInstalled) {
            return;
        }

        window.__cq_authFetchInstalled = true;
        const orig = window.fetch.bind(window);
        window.fetch = (input, init): Promise<Response> => {
            const url = input || '';
            try {
                if (url?.toString().startsWith(chainlitServer)) {
                    const next = init ? { ...init } : {};
                    const headers = new Headers(
                        init?.headers ||
                            (typeof input !== 'string' && input && (input as Request).headers) ||
                            undefined,
                    );

                    if (!headers.has('Authorization')) {
                        headers.set('Authorization', `Bearer ${accessToken}`);
                    }

                    next.headers = headers;

                    return orig(input, next);
                }
            } catch {
                /* empty */
            }

            return orig(input, init);
        };
    }

    private isCopilotOpen(): boolean {
        const shadow = this.connieUiService.getShadowRoot();

        return !!shadow?.querySelector('[role="dialog"]');
    }
    private async loadAndDisplayChainlit(): Promise<void> {
        window.unmountChainlitWidget?.();

        if (!this.hasConnieDataInformation()) {
            return;
        }

        const panel = this.panelRef()?.nativeElement;

        if (!panel || !this.projectBloc.value()) {
            return;
        }

        this.connieUiService.panelRef.set(this.panelRef()!);

        const chainlitConfig = await this.getChainlitConfig();

        if (!chainlitConfig) {
            return;
        }
        window.__cq_chainlit_cfg = chainlitConfig;

        this.installAuthFetch(chainlitConfig.chainlitServer, chainlitConfig.accessToken);
        await this.loadChainlitBundle(chainlitConfig.chainlitServer);

        await this.preflightThreadId(chainlitConfig);

        window.mountChainlitWidget(chainlitConfig);
        window.__connieMounted = true;

        const copilot = document.getElementById('chainlit-copilot');

        if (!copilot) {
            return;
        }

        this.copilotRef.set(copilot);

        if (panel && copilot) {
            panel.appendChild(copilot);
        }

        this.injectStylesIntoChainlitShadow();
        setTimeout(() => {
            window.toggleChainlitCopilot();
            this.isLoading.set(false);
        }, 1000);
    }

    private loadChainlitBundle(chainlitServer: string): Promise<unknown> {
        return new Promise((resolve, reject) => {
            if (window.__chainlitBundleLoaded) {
                return resolve('');
            }

            const scriptElement = document.createElement('script');
            scriptElement.type = 'text/javascript';
            scriptElement.src = `${chainlitServer}/copilot/index.js`;
            scriptElement.async = true;
            scriptElement.onload = (): void => {
                window.__chainlitBundleLoaded = true;
                resolve('');
            };
            scriptElement.onerror = (): void => reject(new Error(`Failed to load bundle: ${scriptElement.src}`));
            document.head.appendChild(scriptElement);
        });
    }

    private async preflightThreadId(chainlitConfig: WidgetConfig): Promise<void> {
        // Read the threadId the same way the widget does
        const threadId: string | null =
            typeof window.getChainlitCopilotThreadId === 'function'
                ? window.getChainlitCopilotThreadId()
                : ((): string | null => {
                      try {
                          return localStorage.getItem(ConnieComponent.THREAD_ID_LOCAL_STORAGE_KEY);
                      } catch {
                          return null;
                      }
                  })();

        if (!threadId) {
            return;
        }

        const isThreadValid: boolean = await this.validateThreadId(chainlitConfig.chainlitServer, threadId);
        if (!isThreadValid) {
            this.clearStoredThreadId();
        }
        this.currentThreadId.set(threadId);
    }

    private requestAnimationFrameAsync(): Promise<unknown> {
        return new Promise((resolve) => requestAnimationFrame(resolve));
    }

    private resetChainlitData(): void {
        if (this.copilotRef()) {
            this.panelRef()?.nativeElement.removeChild(this.copilotRef());
        }

        this.connieUiService.isHistoryOpen.set(false);
        this.currentThreadId.set(null);
        this.copilotRef.set(null);
        window.__chainlitBundleLoaded = false;
        window.__connieMounted = false;
        window.__cq_authFetchInstalled = false;
    }

    private resumeEnhancers(shadow: ShadowRoot): void {
        try {
            // Recreate clean observers rather than reusing old handles
            this.connieUiService.observeShadow(shadow);
        } catch {
            /* empty */
        }
    }

    /** Temporarily pause our enhancement observers during sensitive updates */
    private suspendEnhancers(shadow: ShadowRoot): void {
        try {
            if (shadow.__copilotObserver?.disconnect) {
                shadow.__copilotObserver.disconnect();
                shadow.__copilotObserverPaused = shadow.__copilotObserver;
                shadow.__copilotObserver = null;
            }
            if (shadow.__cqResizeObs?.disconnect) {
                shadow.__cqResizeObs.disconnect();
                shadow.__cqResizeObsPaused = shadow.__cqResizeObs;
                shadow.__cqResizeObs = null;
            }
        } catch {
            /* empty */
        }
    }

    private async validateThreadId(chainlitServer: string, threadId: string): Promise<boolean> {
        const url = `${chainlitServer}/project/thread/${encodeURIComponent(threadId)}`;

        try {
            const res = await fetch(url, { method: 'GET' });
            if (res.status === 200) {
                return true;
            }

            // Treat these as invalid/unauthorized/not found => should clear
            if (res.status === 401 || res.status === 403 || res.status === 404) {
                return false;
            }

            // Any other status: be conservative and treat as invalid
            return false;
        } catch {
            return false;
        }
    }

    private waitForCopilotShadow(cb: (value: ShadowRoot | null) => void, { maxAttempts = 60, delay = 100 } = {}): void {
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            const shadow = this.connieUiService.getShadowRoot();
            if (shadow) {
                clearInterval(interval);
                cb(shadow);

                return;
            }

            if (attempts >= maxAttempts) {
                clearInterval(interval);
                // eslint-disable-next-line no-console
                console.warn('Copilot shadow not found (timeout)');
            }
        }, delay);
    }

    private waitForCopilotShadowAsync(opts = {}): Promise<ShadowRoot | null> {
        return new Promise((resolve) => this.waitForCopilotShadow(resolve, opts));
    }

    /** Wait for the widget DOM to be ready and idle for a couple of frames */
    private async waitForStableWidget({ idleMs = 40, frames = 2 } = {}): Promise<void> {
        await this.waitForCopilotShadowAsync({ delay: 100, maxAttempts: 120 });
        for (let i = 0; i < frames; i++) {
            await this.requestAnimationFrameAsync();
        }
        await this.delay(idleMs);
    }

    private waitUntil(checkFn: () => boolean, { maxAttempts = 120, delay = 100 } = {}): Promise<unknown> {
        return new Promise((resolve, reject) => {
            let attempts = 0;
            const iv = setInterval(() => {
                attempts++;
                try {
                    if (checkFn()) {
                        clearInterval(iv);
                        resolve(true);

                        return;
                    }
                } catch {
                    /* empty */
                }
                if (attempts >= maxAttempts) {
                    clearInterval(iv);
                    reject(new Error('waitUntil: timeout'));
                }
            }, delay);
        });
    }
}
